/**
 * @module Components.Routes
 *
 */
import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import { useForm, FormProvider } from 'react-hook-form';

import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";

import { EventModel } from 'app/models/EventModel';
import { LessonRateModel } from 'app/models/scheduling/LessonRateModel';
import { CoachProfileModel } from 'app/models/scheduling/CoachProfileModel';

import EventTypeSelector from "./EventTypeSelector";
import SessionForm from "./SessionForm";
import ScheduleBlockForm from "./ScheduleBlockForm";
import BillNowConfirmationModal from "./BillNowConfirmationModal";
import { needsBillNowConfirmation } from "./paymentHelpers";
import { sessionCountForRecurrence } from "./recurrenceCount";

import styles from './styles.module.scss';
import CloseIcon from "../../../../public/images/close.svg";
import { useTranslation } from 'react-i18next';
import { FacilityModel } from 'app/models/scheduling/FacilityModel';
import { useActions } from "app/utils/hooks";
import { clearAppliedTemplates, createSpace, getCreatedSpace } from "app/actions/spaceCreation";
import ButtonVariant from 'app/components/Button/ButtonVariant';

type PropsDef = {
  event: EventModel;
  lessonRateOptions: LessonRateModel[];
  onCancel: () => void;
  onSave: (event: EventModel) => void;
  submitting: boolean;
  errors: any;
  coachProfile: CoachProfileModel;
  facility: FacilityModel;
  facilityBookingEnabled?: boolean;
};

export default function EventForm({
  event,
  onCancel,
  lessonRateOptions,
  onSave,
  errors,
  submitting=false,
  coachProfile,
  facility,
  facilityBookingEnabled,
}: PropsDef) {
  const { t } = useTranslation();
  const [availablePackages, setAvailablePackages] = useState([]);

  const methods = useForm({
    defaultValues: {
      ...event,
      coachProfiles: [coachProfile],
      // True after the user picks a value in the Resource field, also the
      // same bay again. A series edit then sends the bay (see bayKeyForScope).
      scheduleResourcePicked: false,
    },
    mode: 'onChange',
  });

  const {
    control,
    register,
    unregister,
    handleSubmit,
    setValue,
    watch,
    formState,
    clearErrors,
    setError,
    getValues,
  } = methods;

  useEffect(() => {
    clearErrors();

    if (!errors) {
      return;
    }

    if (Object.keys(errors).length > 0) {
      Object.keys(errors).forEach((key) => {
        const error = errors[key];
        if (error) {
          let field = key;
          if (field === 'studentProfileIds') {
            field = 'studentProfiles';
          }

          if (field === 'onlinePaymentType') {
            field = 'online_payment_type';
          }
          if (field === 'chargeTiming') {
            field = 'charge_timing';
          }
          if (field === 'payingAthleteId' || field === 'paying_athlete_id') {
            field = 'paying_athlete_id';
          }

          if (field === 'payment_method' || field === 'paymentMethod') {
            field = 'payment_method';
          }
          const message = error.join(', ');
          // @ts-expect-error - setError field type mismatch
          setError(field, { type: 'custom', message });
        }
      });
    }
  }, [errors]);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingBillNowData, setPendingBillNowData] = useState<any>(null);

  const fadeForModal = showConfirmation || !!pendingBillNowData;

  const watchEventType = watch('type');
  const watchStart = watch('start');
  const watchEnd = watch('end');
  const watchTimeZone = watch('timeZone');

  // A session (unlike a multi-day block) must start and end on the same calendar
  // day. A long booking option (e.g. 24h) or a late start would otherwise push
  // the end past midnight, which the calendar can't represent — block it here
  // with a clear message. Blocks (`coach_schedule_block`) may legitimately span
  // days, so they're exempt.
  const crossesMidnight = React.useMemo(() => {
    if (watchEventType === 'coach_schedule_block' || !watchStart || !watchEnd) {
      return false;
    }
    let start = DateTime.fromISO(watchStart);
    let end = DateTime.fromISO(watchEnd);
    if (watchTimeZone) {
      start = start.setZone(watchTimeZone);
      end = end.setZone(watchTimeZone);
    }
    if (!start.isValid || !end.isValid) {
      return false;
    }
    // Mirror the save-time normalization: the end time-of-day is stamped onto the
    // start's day, and a duration whose end is at/before the start wraps to the
    // next day. Evaluating the guard against that same normalized end catches a
    // wrapped range (end time-of-day <= start, e.g. 10:00→09:00) — not just an
    // end already dated past midnight.
    let normalizedEnd = end.set({ year: start.year, month: start.month, day: start.day });
    if (normalizedEnd <= start) {
      normalizedEnd = normalizedEnd.plus({ days: 1 });
    }
    // Allowed up to and including the midnight that closes the start day (a full
    // 00:00→00:00 day, or a session ending exactly at midnight). Only forbidden
    // when the end runs past that midnight into the next day.
    const nextMidnight = start.startOf('day').plus({ days: 1 });
    return normalizedEnd > nextMidnight;
  }, [watchEventType, watchStart, watchEnd, watchTimeZone]);

  const onDateChange = (changeEvent) => {
    const rawDate = changeEvent.target.value;
    const date = rawDate instanceof Date
      ? DateTime.fromJSDate(rawDate)
      : (DateTime.isDateTime(rawDate) ? rawDate : DateTime.fromISO(rawDate));

    setValue('date', rawDate, { shouldValidate: true, shouldDirty: true });
    const startTime = DateTime.fromISO(getValues('start'))
                              .setZone(event.timeZone)
                              .set({
                                year: date.year,
                                month: date.month,
                                day: date.day
                              });
    let endTime = DateTime.fromISO(getValues('end'))
                          .setZone(event.timeZone)
                          .set({
                            year: date.year,
                            month: date.month,
                            day: date.day
                          });
    // A duration that wraps past midnight (end time-of-day <= start) belongs on
    // the next day; keep it there rather than collapsing to a zero-length range.
    if (endTime <= startTime) {
      endTime = endTime.plus({ days: 1 });
    }

    setValue('start', startTime.toISO(), { shouldDirty: true });
    setValue('end', endTime.toISO(), { shouldDirty: true });
  };

  const createSpaceActions = useActions({
    createSpace,
    getCreatedSpace,
    clearAppliedTemplates,
  });

  const registerNewStudents = (data: any) => {
    const newStudents = data.studentProfiles
      .filter((student) => student.newStudent === true)
      .map((student) => {
        return {
          email: student.email,
          name: student.fullName,
          firstName: student.firstName,
          lastName: student.lastName,
          user_id: student.id,
          templateId: student.templateId,
          newSpace: student.newSpace,
        };
      });

    const coaches = data.coachProfiles?.map((coach) => {
      return {
        email: coach.email,
        name: coach.fullName,
        user_id: coach.id,
      };
    });

    newStudents?.map((student: any) => {
      const { templateId, newSpace, ...newStudent } = student;
      if (newSpace === true) {
        const newBaseApplication = {
          athletes: [newStudent],
          coaches,
          create_for_self: false,
          segment_ids: [],
          space_template_id: templateId,
          sport: "Golf",
          sport_other: "Golf",
        };

        createSpaceActions.createSpace({
          spaces_import: newBaseApplication
        } as any);
      }
    });
  };

  const onSubmit = async (data) => {
    // Also block here (not only via the disabled Save button) so implicit submit
    // — e.g. pressing Enter — can't bypass the same-day rule.
    if (crossesMidnight) {
      return;
    }

    if (needsBillNowConfirmation(data, !event.id)) {
      setPendingBillNowData(data);
      return;
    }

    await submitEvent(data);
  };

  const submitEvent = async (data) => {
    if (data.studentProfiles) {
      registerNewStudents(data);
    }

    let eventData;
    if (data.type === 'lesson_schedule') {
      let selectedPackage;
      if (data.payment_method === 'package' && data.selected_package_id) {
        const selectedProduct = availablePackages.find(
          (product) => product.rateLinkId === Number(data.selected_package_id)
        );
        if (selectedProduct) {
          selectedPackage = selectedProduct.rateLinkId;
        }
      }

      eventData = {
        ...data,
        studentProfileIds: data.studentProfiles.map((student) => student.id),
        rate: !data.rate ? 0 : data.rate,
        payingAthleteId: data.paying_athlete_id?.external_id,
        payment_method: data.payment_method,
        online_payment_type: data.online_payment_type,
        charge_timing: data.charge_timing,
        payment_status: data.payment_status === 'unpaid' ? 'pending' : data.payment_status,
        stripeProductRateLinkId: selectedPackage
      };
      delete eventData.studentProfiles;
    } else {
      eventData = data;
    }

    const date = eventData.date instanceof Date
      ? DateTime.fromJSDate(eventData.date)
      : (DateTime.isDateTime(eventData.date) ? eventData.date : DateTime.fromISO(eventData.date));

    const stampedStart = DateTime.fromISO(eventData.start).setZone(eventData.timeZone).set({
      year: date.year,
      month: date.month,
      day: date.day,
    });
    let stampedEnd = DateTime.fromISO(eventData.end).setZone(eventData.timeZone).set({
      year: date.year,
      month: date.month,
      day: date.day,
    });
    // A duration that wraps past midnight (end time-of-day <= start) belongs on
    // the next day; keep it there rather than collapsing to a zero-length range.
    if (stampedEnd <= stampedStart) {
      stampedEnd = stampedEnd.plus({ days: 1 });
    }

    eventData = {
      ...eventData,
      start: stampedStart.toISO(),
      end: stampedEnd.toISO(),
    };

    await onSave(eventData);
  };

  const isEdit = !!event.id;
  const isNew = !isEdit;

  const title = isEdit ? t('Edit Event') : t('New Event');

  const onEventTypeChange = (changeEvent: any) => {
    const eventType = changeEvent.target.value;
    if (eventType === 'lesson_schedule') {
      unregister('title');
      unregister('private');
    }

    if (eventType === 'coach_schedule_block') {
      unregister('studentProfiles', { keepDefaultValue: true });
      unregister('lessonRateId', { keepDefaultValue: true });
      unregister('rate', { keepDefaultValue: true });
      unregister('location', { keepDefaultValue: true });
    }

    setValue('type', eventType);
  };

  const onDismiss = () => {
    const hasDirtyFields = Object.keys(formState.dirtyFields).length > 0;
    if (hasDirtyFields) {
      setShowConfirmation(true);
    } else {
      onCancel();
    }
  };

  return (
    <>
      <ModalLayout isOpen classNames={`${styles.modal} ${fadeForModal ? styles.fade_for_confirmation : ''}  event_form_container`} data-testid="calendar-event-form">
        <div className={styles.header}>
          <h2>{title}</h2>
          <button type="button" className={styles.close_button} onClick={onDismiss} aria-label="Close">
            <img src={CloseIcon} alt="Close" />
          </button>
        </div>

        <div className={`${styles.event_form}`}>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {isNew && <EventTypeSelector {...register('type', { onChange: onEventTypeChange })} control={control} allowUnavailable />}

              {watchEventType === 'coach_schedule_block' ? (
                <ScheduleBlockForm onDateChange={onDateChange} />
              ) : (
                <SessionForm
                  lessonRateOptions={lessonRateOptions}
                  onDateChange={onDateChange}
                  coachProfile={coachProfile}
                  facility={facility}
                  canAddNewAthletes={true}
                  setAvailablePackages={setAvailablePackages}
                  availablePackages={availablePackages}
                  isEdit={isEdit}
                  event={event}
                  facilityBookingEnabled={facilityBookingEnabled}
                />
              )}

            </form>
          </FormProvider>
        </div>

        {crossesMidnight && (
          <div className={styles.cross_day_warning}>
            {t("A session must start and end on the same day. Please pick a shorter duration or an earlier start time.")}
          </div>
        )}

        <div className={styles.form_actions}>
          <ButtonVariant
            title={t("Cancel")}
            buttonType="light-primary"
            onClickFunc={onDismiss}
            data-testid="event-form-cancel-button"
          />
          <ButtonVariant
            disabled={!formState.isValid || submitting || crossesMidnight}
            title={submitting ? t('Saving...') : t('Save')}
            buttonType="primary"
            onClickFunc={handleSubmit(onSubmit)}
            data-testid="event-form-save-button"
          />
        </div>
      </ModalLayout>

      <ModalLayout isOpen={showConfirmation} classNames={styles.confirm_cancel_modal} wrapperClassName="confirm_cancel">
        <div className={styles.confirmation_modal_content}>
          <div>
            {t("Are you sure you want to discard this event?")}
          </div>
          <div className={styles.confirmation_actions}>
            <button className="btn btn-secondary" type="button" onClick={() => setShowConfirmation(false)}>{t("Cancel")}</button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => {
                setShowConfirmation(false);
                onCancel();
              }}>
                {t("Discard")}
            </button>
          </div>
        </div>
      </ModalLayout>

      <BillNowConfirmationModal
        isOpen={!!pendingBillNowData}
        onlinePaymentType={pendingBillNowData?.online_payment_type}
        chargeTiming={pendingBillNowData?.charge_timing}
        currencySymbol={coachProfile?.currencySymbol}
        sessionPrice={Number(pendingBillNowData?.rate) || 0}
        sessionCount={sessionCountForRecurrence(
          pendingBillNowData?.repeat,
          pendingBillNowData?.date,
          pendingBillNowData?.untilDate,
        )}
        onCancel={() => setPendingBillNowData(null)}
        onConfirm={() => {
          const data = pendingBillNowData;
          setPendingBillNowData(null);
          submitEvent(data);
        }}
      />
    </>
  );
}
